import React from 'react';
import ReactDOM from 'react-dom';
import { ActivityIndicator, Modal } from 'zarm';
import { StaticToast } from 'src/components/common';
import storage from 'src/utils/storage';
import { xflowPushEvent } from 'src/utils/pageTrack';
import './rightLoadingModal.scss';

declare const window: Window & {
  reactHistory?: {
    listen: (callback: (location: { pathname: string; search?: string }) => void) => () => void;
  };
};

const RIGHT_LOADING_MESSAGE = '权益处理中，请稍候';
const RIGHT_NONE_MESSAGE = '暂无可用权益';
const RIGHT_EXPIRED_MESSAGE = '权益已过期';
const POLL_INTERVAL_MS = 3000;
const POLL_MAX_ATTEMPTS = 40;
const RELOAD_DELAY_MS = 3000;
const RELOAD_DELAY_SEC = RELOAD_DELAY_MS / 1000;
const MARKER_STORAGE_PREFIX = 'hy_right_loading_modal:';
/** storage.set 的 timeout 单位为分钟，key 最长保留 1 天 */
const MARKER_EXPIRE_MINUTES = 24 * 60;
/** 再进页 intercept「暂无可用权益」的有效窗口（与 storage 过期无关） */
const MARKER_INTERCEPT_WINDOW_MS = 2 * 60 * 1000;
/** 弹窗 pathname 白名单；空数组 = 全站生效 */
const INTERCEPT_PATH_WHITELIST = ['/hospital/mallClaims/ConvenientDrugDirectPay'];

interface PathModalConfig {
  hideTitle?: boolean;
  title?: string;
  desc: string;
  hideContinueButton?: boolean;
  autoPoll?: boolean;
  successInModal?: boolean;
  successToastText?: string;
}

const DEFAULT_PATH_CONFIG: PathModalConfig = {
  title: '未查询到权益',
  desc: '如果您有该权益，预计 1-2 分钟发放完成。您可稍后查询，或点击按钮继续查询',
  hideContinueButton: false,
  autoPoll: false,
  successInModal: true,
};

/** 按 pathname 定制弹窗交互；未配置的路径使用 DEFAULT_PATH_CONFIG */
const PATH_MODAL_CONFIG_MAP: Record<string, Partial<PathModalConfig>> = {
  '/hospital/mallClaims/ConvenientDrugDirectPay': {
    hideTitle: true,
    desc: '权益领取中，预计需要1-2分钟，请稍后...',
    hideContinueButton: true,
    autoPoll: true,
    successInModal: false,
    successToastText: '查询成功，即将刷新页面',
  },
};

const XFLOW_EVENT = {
  MODAL_SHOW: 'RIGHT_LOADING_MODAL_SHOW',
  CLICK_CONTINUE: 'RIGHT_LOADING_MODAL_CONTINUE',
  POLL_SUCCESS: 'RIGHT_LOADING_POLL_SUCCESS',
  POLL_EXHAUSTED: 'RIGHT_LOADING_POLL_EXHAUSTED',
} as const;

type ModalStatus = 'idle' | 'polling' | 'success';

interface FetchOptions {
  url: string;
  type: string;
  data?: object;
  [key: string]: unknown;
}

interface ActiveSession {
  options: FetchOptions;
  resolve: (res: Record<string, unknown>) => void;
  abortPoll: boolean;
  historyUnlisten?: () => void;
  reloadTimer?: ReturnType<typeof setTimeout>;
  reloadCountdownTimer?: ReturnType<typeof setInterval>;
  pollTimer?: ReturnType<typeof setInterval>;
  pollInFlight: boolean;
  pollAttemptCount: number;
  lastPollRes: Record<string, unknown>;
  startLocation: string;
}

interface ModalRenderState {
  visible: boolean;
  status: ModalStatus;
  attemptCount: number;
  reloadCountdown: number;
}

const defaultRenderState: ModalRenderState = {
  visible: false,
  status: 'idle',
  attemptCount: 0,
  reloadCountdown: 0,
};

let mountDiv: HTMLDivElement | null = null;
let activeSession: ActiveSession | null = null;
let renderState: ModalRenderState = { ...defaultRenderState };

function getPageKey() {
  return `${window.location.pathname}${window.location.search}`;
}

function isPathWhitelisted() {
  if (INTERCEPT_PATH_WHITELIST.length === 0) {
    return true;
  }
  return INTERCEPT_PATH_WHITELIST.includes(window.location.pathname);
}

function getPathModalConfig(): PathModalConfig {
  const override = PATH_MODAL_CONFIG_MAP[window.location.pathname] || {};
  return { ...DEFAULT_PATH_CONFIG, ...override };
}

function trackRightLoadingEvent(
  eventTag: string,
  text: string,
  extra: Record<string, unknown> = {},
) {
  xflowPushEvent([
    'click',
    eventTag,
    text,
    {
      extra_infomation: {
        pathname: window.location.pathname,
        ...extra,
      },
    },
  ]);
}

function getMarkerStorageKey(pageKey: string) {
  return `${MARKER_STORAGE_PREFIX}${pageKey}`;
}

function setModalShownMarker(pageKey: string) {
  storage.set(getMarkerStorageKey(pageKey), { shownAt: Date.now() }, MARKER_EXPIRE_MINUTES);
}

function clearModalShownMarker(pageKey: string) {
  storage.remove(getMarkerStorageKey(pageKey));
}

function isWithinMarkerWindow(pageKey: string) {
  const marker = storage.get(getMarkerStorageKey(pageKey)) as { shownAt?: number } | undefined;
  if (!marker?.shownAt || Number.isNaN(marker.shownAt)) {
    return false;
  }
  return Date.now() - marker.shownAt < MARKER_INTERCEPT_WINDOW_MS;
}

function getResponseMessage(res: Record<string, unknown> | null | undefined) {
  return (res?.message as string) || '';
}

function isNetworkPollError(res: Record<string, unknown>) {
  return res.code === 'FETCH_ERROR';
}

/** 状态一：轮询继续 */
function shouldContinuePolling(message: string) {
  return message === RIGHT_LOADING_MESSAGE || message === RIGHT_NONE_MESSAGE;
}

function shouldInterceptResponse(res: Record<string, unknown> | null | undefined) {
  const message = getResponseMessage(res);
  if (message === RIGHT_LOADING_MESSAGE) {
    return true;
  }
  if (message === RIGHT_NONE_MESSAGE) {
    return isWithinMarkerWindow(getPageKey());
  }
  return false;
}

/** 状态二：业务终态，立即停止轮询 */
function isTerminalRightsMessage(message: string) {
  if (!message) {
    return false;
  }
  if (message === RIGHT_EXPIRED_MESSAGE) {
    return true;
  }
  if (message.includes('生效,暂无法使用权益') || message.includes('生效，暂无法使用权益')) {
    return true;
  }
  if (message.includes('您的权益生效时间为')) {
    return true;
  }
  if (message.includes('保单') && message.includes('暂无可用权益')) {
    return true;
  }
  return false;
}

function stopPolling(session: ActiveSession) {
  if (session.pollTimer) {
    clearInterval(session.pollTimer);
    session.pollTimer = undefined;
  }
}

function finishWithTerminalMessage(
  lastRes: Record<string, unknown>,
  options?: { pollExhausted?: boolean; pollAttemptCount?: number },
) {
  if (options?.pollExhausted) {
    trackRightLoadingEvent(XFLOW_EVENT.POLL_EXHAUSTED, '权益加载弹窗_轮询完毕仍失败', {
      pollAttemptCount: options.pollAttemptCount ?? POLL_MAX_ATTEMPTS,
      message: getResponseMessage(lastRes),
    });
  }
  updateRenderState({ visible: false, status: 'idle', attemptCount: 0, reloadCountdown: 0 });
  StaticToast.error(getResponseMessage(lastRes) || '服务异常');
  cleanupSession(lastRes);
}

function updateRenderState(partial: Partial<ModalRenderState>) {
  renderState = { ...renderState, ...partial };
  renderModal();
}

function renderModal() {
  if (!mountDiv) {
    mountDiv = document.createElement('div');
  }
  const config = getPathModalConfig();
  ReactDOM.render(
    <RightLoadingModalComponent
      visible={renderState.visible}
      status={renderState.status}
      attemptCount={renderState.attemptCount}
      reloadCountdown={renderState.reloadCountdown}
      config={config}
      onContinue={() => {
        RightLoadingModal.startPoll();
      }}
    />,
    mountDiv,
  );
}

interface RightLoadingModalComponentProps {
  visible: boolean;
  status: ModalStatus;
  attemptCount: number;
  reloadCountdown: number;
  config: PathModalConfig;
  onContinue: () => void;
}

const RightLoadingModalComponent: React.FC<RightLoadingModalComponentProps> = ({
  visible,
  status,
  attemptCount,
  reloadCountdown,
  config,
  onContinue,
}) => {
  const isDisabled = status !== 'idle';
  const modalClassName = [
    'right-loading-modal',
    config.hideTitle ? 'right-loading-modal--no-title' : '',
    config.hideContinueButton ? 'right-loading-modal--no-footer' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const renderFooterText = () => {
    if (status === 'success') {
      return (
        <div className='right-loading-modal__loading-progress'>
          查询成功，即将刷新
          <span className='right-loading-modal__loading-progress-text'>
            （{reloadCountdown}s）
          </span>
        </div>
      );
    }
    if (status === 'polling') {
      return (
        <div className='right-loading-modal__loading-progress'>
          努力查询中
          <ActivityIndicator className='right-loading-modal__loading' />
          <span className='right-loading-modal__loading-progress-text'>
            （第{attemptCount}次）
          </span>
        </div>
      );
    }
    return '继续查询';
  };

  const renderFooter = () => {
    if (config.hideContinueButton) {
      return null;
    }
    return (
      <div
        className={`right-loading-modal__btn${isDisabled ? ' is-disabled' : ''}`}
        onClick={() => {
          if (!isDisabled) {
            onContinue();
          }
        }}
      >
        {renderFooterText()}
      </div>
    );
  };

  return (
    <Modal
      maskClosable={false}
      closable={false}
      className={modalClassName}
      visible={visible}
      title={
        config.hideTitle ? null : (
          <div className='right-loading-modal__title'>{config.title}</div>
        )
      }
      footer={config.hideContinueButton ? null : renderFooter()}
    >
      <div className='right-loading-modal__desc'>{config.desc}</div>
    </Modal>
  );
};

function clearReloadTimers(session: ActiveSession) {
  if (session.reloadTimer) {
    clearTimeout(session.reloadTimer);
    session.reloadTimer = undefined;
  }
  if (session.reloadCountdownTimer) {
    clearInterval(session.reloadCountdownTimer);
    session.reloadCountdownTimer = undefined;
  }
}

function startReloadCountdown(session: ActiveSession) {
  const config = getPathModalConfig();

  if (!config.successInModal) {
    updateRenderState({ visible: false, status: 'idle', attemptCount: 0, reloadCountdown: 0 });

    const toastText = config.successToastText || '查询成功，即将刷新页面';
    StaticToast.success(toastText, RELOAD_DELAY_MS, true);

    session.reloadTimer = setTimeout(() => {
      window.location.reload();
    }, RELOAD_DELAY_MS);
    return;
  }

  let remaining = RELOAD_DELAY_SEC;
  updateRenderState({ status: 'success', reloadCountdown: remaining });

  session.reloadCountdownTimer = setInterval(() => {
    remaining -= 1;
    if (remaining <= 0) {
      if (session.reloadCountdownTimer) {
        clearInterval(session.reloadCountdownTimer);
        session.reloadCountdownTimer = undefined;
      }
      return;
    }
    updateRenderState({ reloadCountdown: remaining });
  }, 1000);

  session.reloadTimer = setTimeout(() => {
    window.location.reload();
  }, RELOAD_DELAY_MS);
}

function cleanupSession(resolveValue?: Record<string, unknown>) {
  const session = activeSession;
  if (!session) {
    return;
  }

  stopPolling(session);
  clearReloadTimers(session);
  if (session.historyUnlisten) {
    session.historyUnlisten();
  }

  activeSession = null;

  if (resolveValue) {
    session.resolve(resolveValue);
  }
}

async function startPollInterval() {
  const session = activeSession;
  if (!session || session.abortPoll) {
    return;
  }

  const snapshot: FetchOptions = { ...session.options };
  delete snapshot.rightLoadingPoll;

  const { fetchJson } = await import('src/utils/fetch');

  session.pollAttemptCount = 0;
  session.pollInFlight = false;
  session.lastPollRes = { message: RIGHT_LOADING_MESSAGE };

  const tick = async () => {
    if (!activeSession || session.abortPoll || session.pollInFlight) {
      return;
    }

    if (session.pollAttemptCount >= POLL_MAX_ATTEMPTS) {
      stopPolling(session);
      finishWithTerminalMessage(session.lastPollRes, {
        pollExhausted: true,
        pollAttemptCount: session.pollAttemptCount,
      });
      return;
    }

    session.pollAttemptCount += 1;
    session.pollInFlight = true;
    updateRenderState({ status: 'polling', attemptCount: session.pollAttemptCount });

    try {
      session.lastPollRes = (await fetchJson({
        ...snapshot,
        rightLoadingPoll: true,
        needToast: false,
        needLoading: false,
        isloading: false,
      })) as Record<string, unknown>;
    } catch {
      session.lastPollRes = { code: 'FETCH_ERROR', message: '网络异常，请稍后重试' };
    } finally {
      session.pollInFlight = false;
    }

    if (session.abortPoll) {
      updateRenderState({ visible: false, status: 'idle', attemptCount: 0, reloadCountdown: 0 });
      cleanupSession({ code: 'ABORTED', message: '请求已取消' });
      return;
    }

    const lastRes = session.lastPollRes;

    if (lastRes.code === '0') {
      stopPolling(session);
      clearModalShownMarker(getPageKey());
      trackRightLoadingEvent(XFLOW_EVENT.POLL_SUCCESS, '权益加载弹窗_查询成功', {
        pollAttemptCount: session.pollAttemptCount,
      });
      startReloadCountdown(session);
      return;
    }

    if (isNetworkPollError(lastRes)) {
      if (session.pollAttemptCount >= POLL_MAX_ATTEMPTS) {
        stopPolling(session);
        finishWithTerminalMessage(lastRes, {
          pollExhausted: true,
          pollAttemptCount: session.pollAttemptCount,
        });
      }
      return;
    }

    const message = getResponseMessage(lastRes);

    if (isTerminalRightsMessage(message) || !shouldContinuePolling(message)) {
      stopPolling(session);
      finishWithTerminalMessage(lastRes);
      return;
    }

    if (session.pollAttemptCount >= POLL_MAX_ATTEMPTS) {
      stopPolling(session);
      finishWithTerminalMessage(lastRes, {
        pollExhausted: true,
        pollAttemptCount: session.pollAttemptCount,
      });
    }
  };

  void tick();
  session.pollTimer = setInterval(() => {
    void tick();
  }, POLL_INTERVAL_MS);
}

function setupHistoryListener(session: ActiveSession) {
  const history = window.reactHistory;
  if (!history?.listen) {
    return;
  }

  session.historyUnlisten = history.listen((location) => {
    const nextLocation = `${location.pathname}${location.search || ''}`;
    if (nextLocation !== session.startLocation) {
      RightLoadingModal.abort();
    }
  });
}

const RightLoadingModal = {
  intercept(res: Record<string, unknown>, options: FetchOptions): Promise<Record<string, unknown>> | null {
    if (!isPathWhitelisted()) {
      return null;
    }

    if (activeSession) {
      return Promise.resolve(res);
    }

    if (!shouldInterceptResponse(res)) {
      return null;
    }

    const pageKey = getPageKey();
    const config = getPathModalConfig();
    if (getResponseMessage(res) === RIGHT_LOADING_MESSAGE) {
      setModalShownMarker(pageKey);
    }

    return new Promise((resolve) => {
      activeSession = {
        options,
        resolve,
        abortPoll: false,
        pollInFlight: false,
        pollAttemptCount: 0,
        lastPollRes: res,
        startLocation: pageKey,
      };
      setupHistoryListener(activeSession);
      renderState = {
        visible: true,
        status: config.autoPoll ? 'polling' : 'idle',
        attemptCount: 0,
        reloadCountdown: 0,
      };
      renderModal();
      trackRightLoadingEvent(XFLOW_EVENT.MODAL_SHOW, '权益加载弹窗出现', {
        triggerMessage: getResponseMessage(res),
        pageKey,
        autoPoll: !!config.autoPoll,
      });
      if (config.autoPoll) {
        void startPollInterval();
      }
    });
  },

  startPoll() {
    const config = getPathModalConfig();
    if (config.autoPoll || !activeSession || renderState.status !== 'idle') {
      return;
    }
    trackRightLoadingEvent(XFLOW_EVENT.CLICK_CONTINUE, '权益加载弹窗_继续查询');
    updateRenderState({ status: 'polling', attemptCount: 0 });
    void startPollInterval();
  },

  hide() {
    renderState = { ...defaultRenderState };
    renderModal();
  },

  abort() {
    const session = activeSession;
    if (!session) {
      return;
    }
    session.abortPoll = true;
    stopPolling(session);
    clearReloadTimers(session);
    RightLoadingModal.hide();
    cleanupSession({ code: 'ABORTED', message: '请求已取消' });
  },
};

export default RightLoadingModal;
